/**
 * Nationality in English words — every ISO 3166-1 alpha-2 code to its demonym
 * (Comuniverse integration, owner decision 4, 22 Oct 2026).
 *
 * The platform stores a citizenship as its alpha-2 code (`talents.nationality`,
 * `custom_fields.nationalities` — see `country-codes.ts`). A partner that reads
 * the nationality as words ("Portuguese", "Brazilian") gets it from here, so
 * the conversion is one table, not a guess at each call site.
 *
 * Complete by construction: every one of the 249 officially assigned ISO
 * 3166-1 alpha-2 codes has an entry, and `demonyms.spec.ts` asserts the key set
 * is exactly that list and that every country the product offers (`COUNTRIES`)
 * is in it. An uninhabited dependency (Bouvet Island, Heard and McDonald
 * Islands, South Georgia, the US Minor Outlying Islands, the French Southern
 * Territories, the British Indian Ocean Territory, Svalbard and Jan Mayen) has
 * no people of its own: its entry is the nationality of the state it belongs
 * to, the one a person naming it would hold.
 *
 * Every value is at most 60 characters (the longest partner field it feeds).
 */

/** ISO 3166-1 alpha-2 → the English demonym (nationality adjective). */
export const NATIONALITY_DEMONYMS: Readonly<Record<string, string>> = Object.freeze({
  AD: 'Andorran',
  AE: 'Emirati',
  AF: 'Afghan',
  AG: 'Antiguan and Barbudan',
  AI: 'Anguillan',
  AL: 'Albanian',
  AM: 'Armenian',
  AO: 'Angolan',
  AQ: 'Antarctic',
  AR: 'Argentine',
  AS: 'American Samoan',
  AT: 'Austrian',
  AU: 'Australian',
  AW: 'Aruban',
  AX: 'Åland Islander',
  AZ: 'Azerbaijani',
  BA: 'Bosnian and Herzegovinian',
  BB: 'Barbadian',
  BD: 'Bangladeshi',
  BE: 'Belgian',
  BF: 'Burkinabe',
  BG: 'Bulgarian',
  BH: 'Bahraini',
  BI: 'Burundian',
  BJ: 'Beninese',
  BL: 'Barthélemois',
  BM: 'Bermudian',
  BN: 'Bruneian',
  BO: 'Bolivian',
  BQ: 'Caribbean Dutch',
  BR: 'Brazilian',
  BS: 'Bahamian',
  BT: 'Bhutanese',
  BV: 'Norwegian',
  BW: 'Motswana',
  BY: 'Belarusian',
  BZ: 'Belizean',
  CA: 'Canadian',
  CC: 'Cocos Islander',
  CD: 'Congolese',
  CF: 'Central African',
  CG: 'Congolese',
  CH: 'Swiss',
  CI: 'Ivorian',
  CK: 'Cook Islander',
  CL: 'Chilean',
  CM: 'Cameroonian',
  CN: 'Chinese',
  CO: 'Colombian',
  CR: 'Costa Rican',
  CU: 'Cuban',
  CV: 'Cape Verdean',
  CW: 'Curaçaoan',
  CX: 'Christmas Islander',
  CY: 'Cypriot',
  CZ: 'Czech',
  DE: 'German',
  DJ: 'Djiboutian',
  DK: 'Danish',
  DM: 'Dominican',
  DO: 'Dominican',
  DZ: 'Algerian',
  EC: 'Ecuadorian',
  EE: 'Estonian',
  EG: 'Egyptian',
  EH: 'Sahrawi',
  ER: 'Eritrean',
  ES: 'Spanish',
  ET: 'Ethiopian',
  FI: 'Finnish',
  FJ: 'Fijian',
  FK: 'Falkland Islander',
  FM: 'Micronesian',
  FO: 'Faroese',
  FR: 'French',
  GA: 'Gabonese',
  GB: 'British',
  GD: 'Grenadian',
  GE: 'Georgian',
  GF: 'French Guianese',
  GG: 'Channel Islander',
  GH: 'Ghanaian',
  GI: 'Gibraltarian',
  GL: 'Greenlandic',
  GM: 'Gambian',
  GN: 'Guinean',
  GP: 'Guadeloupean',
  GQ: 'Equatorial Guinean',
  GR: 'Greek',
  GS: 'British',
  GT: 'Guatemalan',
  GU: 'Guamanian',
  GW: 'Bissau-Guinean',
  GY: 'Guyanese',
  HK: 'Hong Konger',
  HM: 'Australian',
  HN: 'Honduran',
  HR: 'Croatian',
  HT: 'Haitian',
  HU: 'Hungarian',
  ID: 'Indonesian',
  IE: 'Irish',
  IL: 'Israeli',
  IM: 'Manx',
  IN: 'Indian',
  IO: 'British',
  IQ: 'Iraqi',
  IR: 'Iranian',
  IS: 'Icelandic',
  IT: 'Italian',
  JE: 'Channel Islander',
  JM: 'Jamaican',
  JO: 'Jordanian',
  JP: 'Japanese',
  KE: 'Kenyan',
  KG: 'Kyrgyz',
  KH: 'Cambodian',
  KI: 'I-Kiribati',
  KM: 'Comorian',
  KN: 'Kittitian and Nevisian',
  KP: 'North Korean',
  KR: 'South Korean',
  KW: 'Kuwaiti',
  KY: 'Caymanian',
  KZ: 'Kazakh',
  LA: 'Lao',
  LB: 'Lebanese',
  LC: 'Saint Lucian',
  LI: 'Liechtensteiner',
  LK: 'Sri Lankan',
  LR: 'Liberian',
  LS: 'Mosotho',
  LT: 'Lithuanian',
  LU: 'Luxembourger',
  LV: 'Latvian',
  LY: 'Libyan',
  MA: 'Moroccan',
  MC: 'Monegasque',
  MD: 'Moldovan',
  ME: 'Montenegrin',
  MF: 'Saint-Martinois',
  MG: 'Malagasy',
  MH: 'Marshallese',
  MK: 'Macedonian',
  ML: 'Malian',
  MM: 'Burmese',
  MN: 'Mongolian',
  MO: 'Macanese',
  MP: 'Northern Mariana Islander',
  MQ: 'Martinican',
  MR: 'Mauritanian',
  MS: 'Montserratian',
  MT: 'Maltese',
  MU: 'Mauritian',
  MV: 'Maldivian',
  MW: 'Malawian',
  MX: 'Mexican',
  MY: 'Malaysian',
  MZ: 'Mozambican',
  NA: 'Namibian',
  NC: 'New Caledonian',
  NE: 'Nigerien',
  NF: 'Norfolk Islander',
  NG: 'Nigerian',
  NI: 'Nicaraguan',
  NL: 'Dutch',
  NO: 'Norwegian',
  NP: 'Nepali',
  NR: 'Nauruan',
  NU: 'Niuean',
  NZ: 'New Zealander',
  OM: 'Omani',
  PA: 'Panamanian',
  PE: 'Peruvian',
  PF: 'French Polynesian',
  PG: 'Papua New Guinean',
  PH: 'Filipino',
  PK: 'Pakistani',
  PL: 'Polish',
  PM: 'Saint-Pierrais',
  PN: 'Pitcairn Islander',
  PR: 'Puerto Rican',
  PS: 'Palestinian',
  PT: 'Portuguese',
  PW: 'Palauan',
  PY: 'Paraguayan',
  QA: 'Qatari',
  RE: 'Reunionese',
  RO: 'Romanian',
  RS: 'Serbian',
  RU: 'Russian',
  RW: 'Rwandan',
  SA: 'Saudi',
  SB: 'Solomon Islander',
  SC: 'Seychellois',
  SD: 'Sudanese',
  SE: 'Swedish',
  SG: 'Singaporean',
  SH: 'Saint Helenian',
  SI: 'Slovenian',
  SJ: 'Norwegian',
  SK: 'Slovak',
  SL: 'Sierra Leonean',
  SM: 'Sammarinese',
  SN: 'Senegalese',
  SO: 'Somali',
  SR: 'Surinamese',
  SS: 'South Sudanese',
  ST: 'Santomean',
  SV: 'Salvadoran',
  SX: 'Sint Maartener',
  SY: 'Syrian',
  SZ: 'Swazi',
  TC: 'Turks and Caicos Islander',
  TD: 'Chadian',
  TF: 'French',
  TG: 'Togolese',
  TH: 'Thai',
  TJ: 'Tajik',
  TK: 'Tokelauan',
  TL: 'Timorese',
  TM: 'Turkmen',
  TN: 'Tunisian',
  TO: 'Tongan',
  TR: 'Turkish',
  TT: 'Trinidadian and Tobagonian',
  TV: 'Tuvaluan',
  TW: 'Taiwanese',
  TZ: 'Tanzanian',
  UA: 'Ukrainian',
  UG: 'Ugandan',
  UM: 'American',
  US: 'American',
  UY: 'Uruguayan',
  UZ: 'Uzbek',
  VA: 'Vatican',
  VC: 'Vincentian',
  VE: 'Venezuelan',
  VG: 'British Virgin Islander',
  VI: 'US Virgin Islander',
  VN: 'Vietnamese',
  VU: 'Ni-Vanuatu',
  WF: 'Wallisian and Futunan',
  WS: 'Samoan',
  YE: 'Yemeni',
  YT: 'Mahoran',
  ZA: 'South African',
  ZM: 'Zambian',
  ZW: 'Zimbabwean',
});

/**
 * The English demonym of a citizenship code (`PT` → `Portuguese`), or `null`
 * for anything that is not an ISO 3166-1 alpha-2 code. Case and surrounding
 * space are ignored; an alpha-3 code is not converted here (writers store
 * alpha-2 — `toAlpha2CountryCode` is the boundary that converts).
 */
export function nationalityDemonym(code: string | null | undefined): string | null {
  if (!code) return null;
  const key = code.trim().toUpperCase();
  return Object.prototype.hasOwnProperty.call(NATIONALITY_DEMONYMS, key)
    ? NATIONALITY_DEMONYMS[key]
    : null;
}
